import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { Formik } from 'formik';
import React, { useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { useRecoilStateLoadable, useRecoilValueLoadable } from 'recoil';
import * as Yup from 'yup';

import { LabelledOutline } from '../../../common/final-form-material-ui';
import {
  FormikTextField,
  MaterialAutocomplete,
} from '../../../common/formik-material-ui';
import { useBusinessPermissions } from '../../../common/helperFunctions/useBusinessPermissions';
import { trackSaveAddTeam } from '../../../common/userTracking';
import { teamAgentIds } from '../../../recoil/atoms/teamAgentIds';
import { user } from '../../../recoil/atoms/user';
import { methodNameEnum } from '../../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../services/rest/makeAPIRequest';
import dataTableScss from '../../modules/Manage/pages/queues-table/data-table.module.css';
import { DataTable } from '../../modules/Manage/pages/queues-table/DataTable';

import { ModalWithStyles } from './ModalWithStyles';

export function AddTeamModal({
  show,
  handleClose,
  setRefetchToggleForBusinessTeams,
  enqueueSnackbarWithStyles,
}) {
  const useStyles = makeStyles((theme) => ({
    field: {
      marginTop: theme.spacing(1), // space between fields
      marginBottom: theme.spacing(1), // space between fields
    },
    deleteIconButton: {
      color: theme?.custom?.button?.delete?.main,
    },
    button: {
      marginLeft: '40px',
    },
  }));
  const classes = useStyles();
  const addTeamSchema = useMemo(
    () =>
      Yup.object({
        teamName: Yup.string().required('Required'),
      }),
    []
  );
  const [userIds, setUserIds] = useState([]);
  const [confirmedDeleteId, setConfirmedDeleteId] = useState([]);
  const [deleteGroupId, setDeleteGroupId] = useState('');

  const [agentIdsQueued, setAgentIdsQueued] = useRecoilStateLoadable(
    teamAgentIds
  );
  function addToQueue() {
    const idsToAdd = userIds.map((user) => user.userid);

    const queuedIds = idsToAdd
      .filter((userid) => !confirmedDeleteId.includes(userid)) // do not include userids which can be removed from the confirmedDeleteId array
      .concat(agentIdsQueued.contents);

    // if an added userid was deleted previously then re-add it
    // by removing the userid from the confirmedDeleteId array
    const newConfirmedDeletedIds = confirmedDeleteId.filter(
      (userid) => !idsToAdd.includes(userid)
    );

    setConfirmedDeleteId(newConfirmedDeletedIds);
    setAgentIdsQueued(queuedIds);
    setUserIds([]);
  }
  const [userAtom, setUserAtom] = useRecoilStateLoadable(user);
  const dataUsers = {
    call: methodNameEnum.GET_BUSINESS_USERS,
  };
  const businessUsersLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify(dataUsers),
    })
  );
  const onChangeForAutocomplete = (event, newValue) => {
    // keep autocomplete in sync
    setUserIds(
      newValue // update value for type
    );
  };
  const users = useMemo(
    () =>
      businessUsersLoadable.state === 'hasValue'
        ? businessUsersLoadable.contents?.response?.data?.result.filter(
            (user) => user.isactive === 1
          )
        : [],
    [businessUsersLoadable, userAtom]
  );
  const teamSettingCached = useRef([]);
  teamSettingCached.current = useMemo(
    () =>
      (
        businessUsersLoadable.contents?.response?.data?.result
          ?.filter((user) => agentIdsQueued.contents.includes(user.userid))
          ?.map((user) => ({
            email: user.email,
            fname: user.fname,
            lname: user.lname,
            fullName: `${user.fname} ${user.lname}`,
            id: user.userid,
            userEXT: user.userEXT,
            userTier:
              user.userTier === 'Enterprise'
                ? 'Professional'
                : user.userTier === 'Enterprise Unlimited'
                ? 'Professional Unlimited'
                : user.userTier,
            userid: user.userid,
            // insert queue flag to show this member is queued for addition
            queued: true,
          })) ?? []
      )
        .filter((member) => !confirmedDeleteId.includes(member.userid))
        .sort((a, b) =>
          a?.fullName?.toLowerCase()?.localeCompare(b?.fullName?.toLowerCase())
        ),
    [agentIdsQueued, confirmedDeleteId, businessUsersLoadable]
  );
  const arrayOfIdsToFilter = teamSettingCached.current?.map(
    (team) => team.userid
  );
  const mutation = useMutation(getApiMutation(userAtom.contents, setUserAtom));
  const readOnly = useBusinessPermissions().accountReadOnly;

  function formatter(params) {
    // create Actions element here
    // refactor to group id stuff
    return deleteGroupId === params.row.id ? (
      <div className={classes.actionButton}>
        <article className="row">
          <Tooltip title="Confirm Deletion">
            <IconButton
              aria-label="accept"
              onClick={async (event) => {
                event.preventDefault(); // stop link
                // add to the queue for deletion
                setConfirmedDeleteId([...confirmedDeleteId, deleteGroupId]);
                setDeleteGroupId(undefined); // clear the user pending delete confirmation
              }}
              disabled={mutation.isLoading}
              size="large"
            >
              <CheckIcon color={mutation.isLoading ? 'disabled' : 'primary'} />
              {mutation.isLoading && (
                <CircularProgress
                  size={43}
                  className={classes.buttonProgress}
                />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton
              aria-label="cancel"
              onClick={(event) => {
                event.preventDefault(); // stop link
                // reset groupid hide confirm / cancel deletion
                setDeleteGroupId(undefined);
              }}
              disabled={mutation.isLoading}
              size="large"
            >
              <ClearIcon
                color="disabled"
                className={clsx({
                  [classes.deleteIconButton]: !mutation.isLoading, // || deleteCancelTokenSource,
                })}
              />
            </IconButton>
          </Tooltip>
        </article>
      </div>
    ) : (
      <div>
        <Tooltip title="Delete">
          <IconButton
            style={{ marginLeft: '35px' }}
            aria-label="delete"
            onClick={() => setDeleteGroupId(params.row.id)}
            size="large"
          >
            <DeleteIcon className={classes.deleteIconButton} />
          </IconButton>
        </Tooltip>
      </div>
    );
  }
  const columns = [
    {
      field: 'fullName',
      headerName: 'Full Name',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'email',
      headerName: 'Email',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'userEXT',
      headerName: 'Ext.',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'userTier',
      headerName: 'Tier',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'userid',
      headerName: 'Actions',
      headerAlign: 'right',
      align: 'right',
      hide: readOnly,
      enableSorting: false,
      renderCell: formatter,
    },
  ];

  const [sortModel, setGridSortModel] = React.useState([
    { field: 'fullname', sort: 'asc' },
  ]);

  return (
    <Formik
      validationSchema={addTeamSchema}
      onSubmit={async (values, actions) => {
        const data = {
          call: methodNameEnum.SUBMIT_NEW_TEAM,
          apikey: userAtom.contents?.apikey,
          businessid: userAtom.contents?.businessid,
          teamname: values.teamName,
        };
        const response = await mutation.mutateAsync({
          endpoint: endpointEnum.SUBMIT_NEW_TEAM,
          payload: data,
        });
        if (response?.data?.success) {
          const newValues = teamSettingCached.current.filter(
            (member) => member.queued
          );
          const idArr = newValues.flatMap(({ userid }) => [userid]);
          const dataAddAgent = {
            call: methodNameEnum.ADD_AGENT_TEAM,
            apikey: userAtom.contents.apikey,
            businessid: userAtom.contents.businessid,
            teamid: response?.data?.result?.[0]?.teamid,
            ids: JSON.stringify(idArr),
          };
          await mutation.mutateAsync({
            endpoint: endpointEnum.ADD_AGENT_TEAM,
            payload: dataAddAgent,
          });
          setAgentIdsQueued([]);
          setConfirmedDeleteId([]);
          setDeleteGroupId('');
          enqueueSnackbarWithStyles({
            message: `Successfully Added ${values.teamName}`,
            alertProps: { severity: 'success' },
          });
        } else {
          enqueueSnackbarWithStyles({
            message: `Error adding team duplicate team name - ${values.teamName}`,
            alertProps: { severity: 'error' },
          });
        }
        setRefetchToggleForBusinessTeams({});
        handleClose();
        actions.resetForm({});
      }}
      enableReinitialize={true}
      initialValues={{
        teamName: '',
      }}
    >
      {(formik) => (
        <>
          <form
            className="form form-label-right"
            onSubmit={formik.handleSubmit}
            noValidate
          >
            {/* <ModalWithStyles
              onSubmit={formik.handleSubmit}
              title="Add New Team"
              show={show}
              onHide={handleClose}
              disableSubmit={formik.values.teamName.length < 1}
              isSubmitting={formik.isSubmitting}
              isLoading={formik.isSubmitting}
              size={'sm'}
            >
              <FormikTextField
                name="teamName"
                className={classes.field}
                formik={formik}
                fullWidth
                label="Team Name"
                size="small"
                inputProps={{ maxLength: 45 }}
                required
                variant="outlined"
              />
              <br />
              <br />
            </ModalWithStyles> */}
            <ModalWithStyles
              onSubmit={() => {
                trackSaveAddTeam();
                formik.handleSubmit();
              }}
              title="Add A New Team"
              aria-labelledby="example-modal-sizes-title-lg"
              show={show}
              onHide={handleClose}
              isSubmitting={formik.isSubmitting}
              isLoading={formik.isSubmitting}
              disableSubmit={!formik.isValid}
            >
              <FormikTextField
                name="teamName"
                className={classes.field}
                formik={formik}
                fullWidth
                label="Team Name"
                size="small"
                inputProps={{ maxLength: 45 }}
                required
                variant="outlined"
              />
              <LabelledOutline label="Team Members">
                <div className="row">
                  <div className="col-sm-10">
                    <MaterialAutocomplete
                      type="text"
                      fullWidth
                      label="Team Member"
                      size="small"
                      variant="outlined"
                      multiple={true}
                      value={userIds || []}
                      options={
                        users
                          ?.filter(
                            (user) => !arrayOfIdsToFilter?.includes(user.userid)
                          )
                          ?.sort((a, b) =>
                            a?.fname
                              ?.toLowerCase()
                              ?.localeCompare(b?.fname?.toLowerCase())
                          ) ?? []
                      }
                      onChange={onChangeForAutocomplete}
                      getOptionLabel={(user) =>
                        `${user?.fname ?? ''} ${user?.lname ?? ''}`
                      }
                      isOptionEqualToValue={(user, value) =>
                        user?.userid === value?.userid
                      }
                      renderOption={(props, option) => {
                        const optionProps = { ...props, key: option.userid };
                        return (
                          <li {...optionProps}>
                            {`${option?.fname} ${option?.lname}`}
                          </li>
                        );
                      }}
                    />
                  </div>
                  <div className="col-sm-2">
                    <Button
                      className={classes.button}
                      name="Add"
                      variant="contained"
                      startIcon={<AddIcon />}
                      color="primary"
                      onClick={addToQueue}
                      disableElevation
                      disabled={
                        businessUsersLoadable.state !== 'hasValue' ||
                        !userIds.length
                      }
                    >
                      Add
                    </Button>
                  </div>
                </div>
                <DataTable
                  // setSelected={setSelected}
                  columns={columns}
                  loading={businessUsersLoadable.state === 'loading'}
                  rows={teamSettingCached.current}
                  // ToolbarComponent={ToolbarComponent}
                  sortModel={sortModel}
                  setGridSortModel={setGridSortModel}
                />
              </LabelledOutline>
            </ModalWithStyles>
          </form>
        </>
      )}
    </Formik>
  );
}
